<template>
  <div class="cost-explorer-page" @click="onPageClick">
    <div class="ce-layout">
      <!-- LEFT: Report parameters panel -->
      <div v-if="showParams" ref="paramsPanel" class="ce-left-panel">
        <div class="panel-header">
          <span class="panel-title">报告参数</span>
          <button class="panel-close" @click="showParams = false">✕</button>
        </div>

        <!-- Time -->
        <div class="section-header" @click="toggleSection('time')">
          <span :class="['section-chevron', { expanded: expandedSections.time }]">
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="section-label">时间</span>
        </div>
        <transition name="slide">
          <div v-show="expandedSections.time" class="section-body section-border-b">
            <div class="field-group">
              <label class="field-label">粒度</label>
              <div class="granularity-btns">
                <button :class="['gran-btn', granularity==='Monthly'?'active':'']" @click="setGranularity('Monthly')">每月</button>
                <button :class="['gran-btn', granularity==='Daily'?'active':'']" @click="setGranularity('Daily')">每天</button>
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">日期范围</label>
              <button class="date-trigger" @click.stop="showDatePicker = !showDatePicker">
                <span class="date-icon">📅</span>
                <span class="date-text">{{ dateRange.start }} — {{ dateRange.end }}</span>
                <span :class="['trigger-chevron', { expanded: showDatePicker }]">
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
              </button>
              <div v-if="showDatePicker" class="date-picker-dropdown" @click.stop>
                <p class="quick-range-title">预设范围（相对今天）</p>
                <div class="quick-range-btns">
                  <button v-for="r in currentQuickRanges" :key="r.label" class="quick-btn" @click="applyQuickRange(r)">{{ r.label }}</button>
                </div>
                <div class="custom-range-block">
                  <p class="quick-range-title">{{ granularity === 'Daily' ? '自定义日期范围' : '自定义月份范围' }}</p>
                  <el-date-picker v-if="granularity === 'Daily'"
                    v-model="customDateRange"
                    type="daterange"
                    range-separator="—"
                    start-placeholder="起始日"
                    end-placeholder="结束日"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                    size="small"
                    style="width: 100%"
                    @change="applyCustomDateRange"
                  />
                  <el-date-picker v-else
                    v-model="customMonthRange"
                    type="monthrange"
                    range-separator="—"
                    start-placeholder="起始月"
                    end-placeholder="结束月"
                    format="YYYY-MM"
                    value-format="YYYY-MM"
                    size="small"
                    style="width: 100%"
                    @change="applyCustomMonthRange"
                  />
                  <p class="custom-range-hint">提示：{{ granularity === 'Daily' ? '建议范围 ≤ 90 天，避免数据点过多。' : '选不含本月的范围可避免 CUR 当月数据延迟。' }}</p>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- Group by -->
        <div class="section-header" @click="toggleSection('groupby')">
          <span :class="['section-chevron', { expanded: expandedSections.groupby }]">
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="section-label">分组依据</span>
        </div>
        <transition name="slide">
          <div v-show="expandedSections.groupby" class="section-body section-border-b">
            <div class="field-row">
              <label class="field-label">维度</label>
              <button class="clear-link" @click="groupBy = '服务'; groupByTagKey = ''; loadData()">清除</button>
            </div>
            <el-select v-model="groupBy" filterable size="small" class="panel-select" @change="onGroupByChange">
              <el-option v-for="g in computedGroupByOptions" :key="g" :value="g" :label="g" />
            </el-select>
            <div v-if="groupBy === '标签'" class="field-group">
              <label class="field-label">标签键</label>
              <el-select v-model="groupByTagKey" filterable size="small" class="panel-select" placeholder="选择标签键" @change="loadData">
                <el-option v-for="tk in tagKeys" :key="tk" :value="tk" :label="tk" />
              </el-select>
            </div>
          </div>
        </transition>

        <!-- Filters -->
        <div class="section-header" @click="toggleSection('filters')">
          <span :class="['section-chevron', { expanded: expandedSections.filters }]">
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="section-label">筛选条件</span>
          <span v-if="totalAppliedFilters > 0" class="section-badge">({{ totalAppliedFilters }})</span>
        </div>
        <transition name="slide">
          <div v-show="expandedSections.filters" class="section-body-filters">
            <div v-if="totalAppliedFilters > 0" class="filter-section-bar">
              <span class="filter-applied-text">{{ totalAppliedFilters }} 个筛选条件已应用</span>
              <button class="clear-all-link" @click="clearAllFilters">全部清除</button>
            </div>

            <!-- Each filter dimension -->
            <div v-for="dim in computedFilterDims" :key="dim" class="aws-filter-row">
              <div class="aws-filter-label-row">
                <span class="aws-filter-label">{{ filterLabels[dim] }}</span>
                <button v-if="filters[dim] && filters[dim].values.length > 0" class="clear-link" @click="clearFilter(dim)">清除</button>
              </div>
              <div class="aws-filter-select-wrap">
                <button class="aws-filter-trigger" @click.stop="toggleDropdown(dim)">
                  <span class="aws-filter-placeholder" :class="{ 'has-value': filters[dim] && filters[dim].values.length > 0 }">
                    {{ (!filters[dim] || filters[dim].values.length === 0) ? `选择 ${filterLabels[dim]}` : `${filters[dim].values.length} 已选择` }}
                  </span>
                  <svg :class="['aws-filter-arrow', { expanded: openFilterDropdown === dim }]" width="10" height="10" viewBox="0 0 10 10"><path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
                <transition name="dropdown">
                  <div v-if="openFilterDropdown === dim" class="aws-filter-panel" @click.stop>
                    <div class="filter-search-wrap">
                      <input type="text" :placeholder="`搜索${filterLabels[dim]}...`"
                        v-model="filterSearch[dim]" class="filter-search-input" />
                    </div>
                    <div class="filter-options">
                      <p v-if="getFilteredOpts(dim).length === 0" class="no-results">无结果</p>
                      <label v-for="opt in getFilteredOpts(dim)" :key="opt.value" class="filter-option">
                        <input type="checkbox" :checked="filters[dim].values.includes(opt.value)" @change="toggleFilterValue(dim, opt.value)" />
                        <span>{{ opt.label }}</span>
                      </label>
                    </div>
                  </div>
                </transition>
              </div>
            </div>

            <!-- Tag filter -->
            <div class="aws-filter-row">
              <div class="aws-filter-label-row">
                <span class="aws-filter-label">标签</span>
                <button v-if="selectedTagKey" class="clear-link" @click="clearTagFilter">清除</button>
              </div>
              <div class="aws-filter-select-wrap">
                <el-select v-model="selectedTagKey" filterable size="small" class="panel-select" placeholder="选择标签键" clearable @change="onTagKeyChange">
                  <el-option v-for="tk in tagKeys" :key="tk" :value="tk" :label="tk" />
                </el-select>
              </div>
              <div v-if="selectedTagKey && tagValueOptions.length > 0" style="margin-top:6px">
                <div class="aws-filter-select-wrap">
                  <button class="aws-filter-trigger" @click.stop="toggleDropdown('tag')">
                    <span class="aws-filter-placeholder" :class="{ 'has-value': selectedTagValues.length > 0 }">
                      {{ selectedTagValues.length === 0 ? '选择标签值' : `${selectedTagValues.length} 已选择` }}
                    </span>
                    <svg :class="['aws-filter-arrow', { expanded: openFilterDropdown === 'tag' }]" width="10" height="10" viewBox="0 0 10 10"><path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                  </button>
                  <transition name="dropdown">
                    <div v-if="openFilterDropdown === 'tag'" class="aws-filter-panel" @click.stop>
                      <div class="filter-options">
                        <label v-for="tv in tagValueOptions" :key="tv" class="filter-option">
                          <input type="checkbox" :checked="selectedTagValues.includes(tv)" @change="toggleTagValue(tv)" />
                          <span>{{ tv }}</span>
                        </label>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- Advanced options -->
        <div class="section-header" @click="toggleSection('advanced')">
          <span :class="['section-chevron', { expanded: expandedSections.advanced }]">
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
          <span class="section-label">高级选项</span>
        </div>
        <transition name="slide">
          <div v-show="expandedSections.advanced" class="section-body section-border-b">
            <div class="field-group">
              <label class="field-label">成本指标</label>
              <div class="fixed-value">未混合成本</div>
            </div>
          </div>
        </transition>
      </div>

      <!-- RIGHT: Main content -->
      <div class="ce-right-content">
        <div class="sub-header">
          <h1 class="sub-title">成本和使用情况报告</h1>
          <div class="sub-actions">
            <button v-if="!showParams" class="btn-outline btn-sm" @click="showParams = true">⚙ 报告参数</button>
            <button class="btn-outline btn-sm" :disabled="!months.length || !services.length" @click="exportCsv">⬇ 下载 CSV</button>
          </div>
        </div>

        <div class="main-body">
          <!-- KPI + Chart card -->
          <div class="chart-card">
            <div class="chart-card-header">
              <h2 class="chart-card-title">成本和使用情况图</h2>
              <span class="info-link">ⓘ 信息</span>
            </div>
            <div class="kpi-row">
              <div class="kpi-item">
                <p class="kpi-label">总成本</p>
                <p class="kpi-value">${{ Math.round(totalCost).toLocaleString() }}</p>
              </div>
              <div class="kpi-item">
                <p class="kpi-label">每月平均成本</p>
                <p class="kpi-value">${{ Math.round(avgMonthlyCost).toLocaleString() }}</p>
              </div>
              <div class="kpi-item">
                <p class="kpi-label">服务 计数</p>
                <p class="kpi-value">{{ serviceCount }}</p>
              </div>
            </div>
            <div class="chart-type-toggle">
              <span class="costs-label">成本 ($)</span>
              <div class="toggle-group">
                <button v-for="t in chartTypes" :key="t.type"
                  :class="['toggle-btn', { active: chartType === t.type }]"
                  :title="t.title" @click="chartType = t.type">{{ t.icon }}</button>
              </div>
            </div>
            <div ref="mainChartRef" class="chart-container"></div>
          </div>

          <!-- Breakdown Table -->
          <div class="breakdown-card">
            <div class="breakdown-header">
              <h3 class="breakdown-title">成本和使用情况明细</h3>
              <div class="search-wrap">
                <span class="search-icon">🔍</span>
                <input type="text" placeholder="搜索服务..." v-model="searchQuery" class="search-input" />
              </div>
            </div>
            <div class="table-wrap">
              <table class="breakdown-table">
                <thead>
                  <tr>
                    <th class="th-left">服务</th>
                    <th class="th-right">服务总计</th>
                    <th v-for="m in last6Months" :key="m.month" class="th-right">{{ m.month.length === 6 ? m.month.slice(0,4) + '-' + m.month.slice(4) : m.month.slice(0, 7) }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="total-row">
                    <td class="td-left td-bold">总成本</td>
                    <td class="td-right td-bold font-data">${{ Math.round(totalCost).toLocaleString() }}</td>
                    <td v-for="m in last6Months" :key="m.month" class="td-right td-bold font-data">${{ Math.round(m.total).toLocaleString() }}</td>
                  </tr>
                  <tr v-for="row in filteredBreakdown" :key="row.key" class="data-row">
                    <td class="td-left">
                      <span class="color-dot" :style="{ backgroundColor: serviceColors[row.key] }"></span>
                      <span>{{ row.service }}</span>
                    </td>
                    <td class="td-right font-data td-medium">${{ Math.round(row.total).toLocaleString() }}</td>
                    <td v-for="mv in row.monthValues" :key="mv.month" class="td-right font-data td-muted">${{ Math.round(mv.value).toLocaleString() }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { ajaxGet } from '@/api/request'

const PALETTE = ['#4a7fc1', '#7b68b8', '#3d9e7a', '#c4894a', '#c05c5c', '#3d8fa8', '#9aa5b4']

export default {
  name: 'CostExplorer',
  data() {
    return {
      showParams: true,
      chartType: 'bar',
      granularity: 'Monthly',
      groupBy: '服务',
      costMetric: 'unblended',
      searchQuery: '',
      dateRange: { start: '', end: '' },
      customMonthRange: [],
      customDateRange: [],
      showDatePicker: false,
      expandedSections: { time: true, groupby: true, filters: true, advanced: false },
      userType: Number((JSON.parse(localStorage.getItem('userInfo') || '{}').type || 0)),
      curRole: localStorage.getItem('curRole') || '',
      filters: {
        service: { mode: 'Include only', values: [] },
        payer: { mode: 'Include only', values: [] },
        link: { mode: 'Include only', values: [] },
        region: { mode: 'Include only', values: [] },
        instanceType: { mode: 'Include only', values: [] },
        usageType: { mode: 'Include only', values: [] },
        chargeType: { mode: 'Include only', values: [] },
        agent: { mode: 'Include only', values: [] },
        sales: { mode: 'Include only', values: [] },
      },
      openFilterDropdown: null,
      filterSearch: { service: '', payer: '', link: '', region: '', instanceType: '', usageType: '', chargeType: '', agent: '', sales: '' },
      filterLabels: {
        service: '服务', payer: '关联 Payer', link: '关联 Link', region: '区域',
        instanceType: '实例类型', usageType: '使用类型', chargeType: '成本类别',
        agent: '代理商', sales: '销售',
      },
      tagKeys: [],
      groupByTagKey: '',
      selectedTagKey: '',
      selectedTagValues: [],
      tagValueOptions: [],
      QUICK_RANGES_MONTHLY: [
        { label: '过去 1 个月', months: 1 },
        { label: '过去 3 个月', months: 3 },
        { label: '过去 6 个月', months: 6 },
        { label: '过去 12 个月', months: 12 },
        { label: '当前月', months: 0 },
        { label: '年初至今', months: -1 },
      ],
      QUICK_RANGES_DAILY: [
        { label: '过去 7 天', days: 7 },
        { label: '过去 14 天', days: 14 },
        { label: '过去 30 天', days: 30 },
        { label: '过去 60 天', days: 60 },
        { label: '过去 90 天', days: 90 },
        { label: '本月至今', monthToDate: true },
      ],
      chartTypes: [
        { type: 'bar', icon: '▥', title: 'Bar' },
        { type: 'line', icon: '📈', title: 'Line' },
        { type: 'area', icon: '▤', title: 'Area' },
      ],
      months: [],
      services: [],
      serviceData: {},
      totalByService: {},
      monthlyTotals: [],
      grandTotal: 0,
      avgMonthlyCost: 0,
      serviceCount: 0,
      serviceColors: {},
      filterOptions: { service: [], payer: [], link: [], region: [], chargeType: [], instanceType: [], usageType: [], agent: [], sales: [] },
      agentAccountMap: {}, // 代理商 → link 映射
      agentNameToIdMap: {},
      linkPayerMap: {}, // link → payer 映射（payer/link 联动）
      loading: false,
      mainChart: null,
      _loadDataSeq: 0,
      _loadFiltersSeq: 0,
    }
  },
  computed: {
    canUseOrgDims() {
      return this.userType === 1 || this.userType === 3
    },
    computedGroupByOptions() {
      // Daily 模式无 tags 列：移除"标签"分组维度
      const base = ['服务', '关联Payer', '关联Link', '区域', '实例类型', '使用类型', '费用类别']
      if (this.granularity !== 'Daily') base.push('标签')
      if (this.canUseOrgDims) {
        return [...base, '代理商', '销售']
      }
      return base
    },
    currentQuickRanges() {
      return this.granularity === 'Daily' ? this.QUICK_RANGES_DAILY : this.QUICK_RANGES_MONTHLY
    },
    computedFilterDims() {
      const base = ['service', 'payer', 'link', 'region', 'instanceType', 'usageType', 'chargeType']
      if (this.canUseOrgDims) {
        return [...base, 'agent', 'sales']
      }
      return base
    },
    totalCost() { return this.grandTotal },
    last6Months() {
      const n = Math.min(6, this.months.length)
      const start = this.months.length - n
      return this.months.slice(start).map((m, i) => ({
        month: m,
        total: this.monthlyTotals[start + i] || 0,
      }))
    },
    serviceBreakdown() {
      return this.services.map(svc => {
        const total = this.totalByService[svc] || 0
        const n = Math.min(6, this.months.length)
        const start = this.months.length - n
        const monthValues = this.months.slice(start).map((m, i) => ({
          month: m,
          value: (this.serviceData[svc] || [])[start + i] || 0,
        }))
        return { service: svc, key: svc, total, monthValues }
      }).sort((a, b) => b.total - a.total)
    },
    filteredBreakdown() {
      return this.serviceBreakdown.filter(s => s.service.toLowerCase().includes(this.searchQuery.toLowerCase()))
    },
    totalAppliedFilters() {
      let count = 0
      for (const dim of this.computedFilterDims) {
        if (this.filters[dim]) count += this.filters[dim].values.length
      }
      if (this.selectedTagValues.length > 0) count += this.selectedTagValues.length
      return count
    },
  },
  watch: {
    chartType() { this.$nextTick(() => this.renderChart()) },
    'filters.agent.values'(val) {
      if (!this.canUseOrgDims) return
      if (!val || val.length === 0) return
      let agentAccounts = []
      val.forEach(agent => {
        const id = this.agentNameToIdMap[agent] || agent
        if (this.agentAccountMap[id]) agentAccounts.push(...this.agentAccountMap[id])
        if (this.agentAccountMap[agent]) agentAccounts.push(...this.agentAccountMap[agent])
      })
      const valid = new Set(agentAccounts)
      if (this.filters.link.values.some(a => !valid.has(a))) {
        this.filters.link.values = this.filters.link.values.filter(a => valid.has(a))
      }
    },
    'filters.payer.values'(val) {
      // 选了 payer 后，收窄已选的 link 到这些 payer 名下
      if (!val || val.length === 0) return
      const sel = new Set(val)
      if (this.filters.link.values.some(lk => !(this.linkPayerMap[lk] || []).some(p => sel.has(p)))) {
        this.filters.link.values = this.filters.link.values.filter(lk => (this.linkPayerMap[lk] || []).some(p => sel.has(p)))
      }
    },
  },
  mounted() {
    this.initDateRange()
    if (!this.canUseOrgDims) {
      this.filters.agent = { mode: 'Include only', values: [] }
      this.filters.sales = { mode: 'Include only', values: [] }
      if (this.groupBy === '代理商' || this.groupBy === '销售') {
        this.groupBy = '服务'
      }
    }
    this.loadFilters()
    this.loadData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    if (this.mainChart) { this.mainChart.dispose(); this.mainChart = null }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    onPageClick(e) {
      if (this.openFilterDropdown) {
        this.openFilterDropdown = null
      }
      if (this.showDatePicker) {
        this.showDatePicker = false
      }
    },
    initDateRange() {
      const now = new Date()
      const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      const start = new Date(now)
      start.setFullYear(start.getFullYear() - 1)
      start.setMonth(start.getMonth() + 1)
      start.setDate(1)
      this.dateRange = {
        start: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`,
        end,
      }
    },
    dateToMonth(dateStr) {
      return dateStr.replace(/-/g, '').slice(0, 6)
    },
    buildGroupByParam() {
      const map = {
        '服务': 'Service', '关联Link': 'LinkedAccount', '关联账户': 'LinkedAccount',
        '关联Payer': 'LinkedPayer', '区域': 'Region',
        '费用类别': 'ChargeType', '实例类型': 'InstanceType', '使用类型': 'UsageType',
        '代理商': 'Agent', '销售': 'Sales',
      }
      if (this.groupBy === '标签' && this.groupByTagKey) {
        return 'Tag:' + this.groupByTagKey
      }
      return map[this.groupBy] || 'Service'
    },
    onGroupByChange() {
      if (this.groupBy !== '标签') {
        this.groupByTagKey = ''
        this.loadData()
      }
    },
    async loadFilters() {
      const seq = ++this._loadFiltersSeq
      try {
        const params = this.buildTimeParams()
        if (this.filters.service.values.length > 0) params.services = this.filters.service.values.join(',')
        if (this.filters.payer.values.length > 0) params.payers = this.filters.payer.values.join(',')
        if (this.filters.link.values.length > 0) params.accounts = this.filters.link.values.join(',')
        if (this.filters.region.values.length > 0) params.regions = this.filters.region.values.join(',')
        if (this.filters.chargeType.values.length > 0) params.charge_types = this.filters.chargeType.values.join(',')
        if (this.filters.instanceType.values.length > 0) params.instance_types = this.filters.instanceType.values.join(',')
        if (this.filters.usageType.values.length > 0) params.usage_types = this.filters.usageType.values.join(',')
        if (this.canUseOrgDims && this.filters.agent.values.length > 0) {
          const agentIds = this.filters.agent.values.map(v => this.agentNameToIdMap[v] || v)
          params.agents = agentIds.join(',')
        }
        if (this.canUseOrgDims && this.filters.sales.values.length > 0) params.sales = this.filters.sales.values.join(',')

        const res = await ajaxGet({ url: '/stat/cost_explorer/filters/', params })
        if (seq !== this._loadFiltersSeq) return // 竞态守卫：只接受最后一次请求
        if (res) {
          // 构建代理商-账号映射
          let agentAccountMap = {}
          let agentNameToIdMap = {}
          ;(res.agents || []).forEach(a => {
            if (a && a.name && a.id) {
              agentNameToIdMap[a.name] = a.id
            }
          })
          const linkPayerMap = {}
          if (res.accounts && Array.isArray(res.accounts)) {
            res.accounts.forEach(acc => {
              const linkId = acc.id
              // 代理商 → link 映射（值为 link_id）
              if (acc.agent_id) {
                if (!agentAccountMap[acc.agent_id]) agentAccountMap[acc.agent_id] = []
                agentAccountMap[acc.agent_id].push(linkId)
              }
              if (acc.agent_name) {
                if (!agentAccountMap[acc.agent_name]) agentAccountMap[acc.agent_name] = []
                agentAccountMap[acc.agent_name].push(linkId)
              }
              // link → payer 映射
              linkPayerMap[linkId] = Array.isArray(acc.payer_ids) ? acc.payer_ids : []
            })
          }
          this.agentAccountMap = agentAccountMap
          this.agentNameToIdMap = agentNameToIdMap
          this.linkPayerMap = linkPayerMap

          const mkOpt = (id, name) => ({ value: id, label: (name && name !== id) ? `${name} (${id})` : String(id) })
          this.filterOptions = {
            service: res.services || [],
            payer: (res.payers || []).map(p => mkOpt(p.id, p.name)),
            link: (res.accounts || []).map(a => mkOpt(a.id, a.name)),
            region: res.regions || [],
            instanceType: res.instance_types || [],
            usageType: res.usage_types || [],
            chargeType: res.charge_types || [],
            agent: (res.agents || []).map(a => a.name || a.id),
            sales: res.sales || [],
          }
          this.tagKeys = res.tag_keys || []
        }
      } catch (e) {
        console.error('loadFilters error:', e)
      }
    },
    async loadData() {
      const seq = ++this._loadDataSeq
      this.loading = true
      try {
        const params = {
          ...this.buildTimeParams(),
          group_by: this.buildGroupByParam(),
          top_n: 6,
        }
        if (this.filters.service.values.length > 0) {
          if (this.filters.service.mode === 'Include only') {
            params.services = this.filters.service.values.join(',')
          } else {
            params.exclude_services = this.filters.service.values.join(',')
          }
        }
        if (this.filters.payer.values.length > 0) params.payers = this.filters.payer.values.join(',')
        if (this.filters.link.values.length > 0) params.accounts = this.filters.link.values.join(',')
        if (this.filters.region.values.length > 0) params.regions = this.filters.region.values.join(',')
        if (this.filters.instanceType.values.length > 0) params.instance_types = this.filters.instanceType.values.join(',')
        if (this.filters.usageType.values.length > 0) params.usage_types = this.filters.usageType.values.join(',')
        if (this.filters.chargeType.values.length > 0) params.charge_types = this.filters.chargeType.values.join(',')
        if (this.canUseOrgDims && this.filters.agent.values.length > 0) {
          const agentIds = this.filters.agent.values.map(v => this.agentNameToIdMap[v] || v)
          params.agents = agentIds.join(',')
        }
        if (this.canUseOrgDims && this.filters.sales.values.length > 0) params.sales = this.filters.sales.values.join(',')
        if (this.selectedTagKey && this.selectedTagValues.length > 0) {
          params.tag_filters = this.selectedTagValues.map(v => `${this.selectedTagKey}:${v}`).join(',')
        }
        const res = await ajaxGet({ url: '/stat/cost_explorer/service_trend/', params })
        if (seq !== this._loadDataSeq) return // 竞态守卫：只接受最后一次请求
        if (res) {
          this.months = res.months || []
          this.services = res.services || []
          this.serviceData = res.data || {}
          this.totalByService = res.total_by_service || {}
          this.monthlyTotals = res.monthly_totals || []
          this.grandTotal = res.grand_total || 0
          this.avgMonthlyCost = res.avg_monthly || 0
          this.serviceCount = res.service_count || 0
          const colors = {}
          this.services.forEach((s, i) => { colors[s] = PALETTE[i % PALETTE.length] })
          this.serviceColors = colors
          this.$nextTick(() => this.renderChart())
        }
      } catch (e) {
        console.error('loadData error:', e)
      } finally {
        if (seq === this._loadDataSeq) this.loading = false
      }
    },
    handleResize() { if (this.mainChart) this.mainChart.resize() },
    toggleSection(key) {
      this.expandedSections[key] = !this.expandedSections[key]
    },
    toggleDropdown(dim) {
      this.openFilterDropdown = this.openFilterDropdown === dim ? null : dim
    },
    toggleFilterValue(dim, val) {
      const cur = this.filters[dim].values
      if (cur.includes(val)) { this.filters[dim].values = cur.filter(v => v !== val) }
      else { this.filters[dim].values = [...cur, val] }
      this.loadFilters()
      this.loadData()
    },
    clearFilter(dim) {
      this.filters[dim] = { mode: 'Include only', values: [] }
      this.loadFilters()
      this.loadData()
    },
    clearAllFilters() {
      this.computedFilterDims.forEach(dim => { this.filters[dim] = { mode: 'Include only', values: [] } })
      this.selectedTagKey = ''
      this.selectedTagValues = []
      this.tagValueOptions = []
      this.loadFilters()
      this.loadData()
    },
    getFilteredOpts(dim) {
      const search = (this.filterSearch[dim] || '').toLowerCase()
      // 归一化为 {value, label}（字符串维度包一层）
      let opts = (this.filterOptions[dim] || []).map(o =>
        (o && typeof o === 'object') ? o : { value: o, label: String(o) }
      )
      // 关联 link 维度：受「代理商」和「关联 payer」联动收窄
      if (dim === 'link') {
        if (this.canUseOrgDims && this.filters.agent.values.length > 0) {
          const agentAccounts = new Set()
          this.filters.agent.values.forEach(agent => {
            const id = this.agentNameToIdMap[agent] || agent
            ;(this.agentAccountMap[id] || []).forEach(a => agentAccounts.add(a))
            ;(this.agentAccountMap[agent] || []).forEach(a => agentAccounts.add(a))
          })
          opts = opts.filter(o => agentAccounts.has(o.value))
        }
        if (this.filters.payer.values.length > 0) {
          const sel = new Set(this.filters.payer.values)
          opts = opts.filter(o => (this.linkPayerMap[o.value] || []).some(p => sel.has(p)))
        }
      }
      if (!search) return opts
      return opts.filter(o => String(o.value).toLowerCase().includes(search) || String(o.label).toLowerCase().includes(search))
    },
    async onTagKeyChange(val) {
      this.selectedTagValues = []
      this.tagValueOptions = []
      if (!val) { this.loadData(); return }
      try {
        const res = await ajaxGet({
          url: '/stat/cost_explorer/tag_values/',
          params: {
            tag_key: val,
            ...this.buildTimeParams(),
          }
        })
        if (res) this.tagValueOptions = res.values || []
      } catch (e) { console.error('loadTagValues error:', e) }
      this.loadData()
    },
    toggleTagValue(val) {
      if (this.selectedTagValues.includes(val)) {
        this.selectedTagValues = this.selectedTagValues.filter(v => v !== val)
      } else {
        this.selectedTagValues = [...this.selectedTagValues, val]
      }
      this.loadData()
    },
    clearTagFilter() {
      this.selectedTagKey = ''
      this.selectedTagValues = []
      this.tagValueOptions = []
      this.loadData()
    },
    applyQuickRange(r) {
      const now = new Date()
      let start, end
      if (this.granularity === 'Daily') {
        end = new Date(now)
        if (r.monthToDate) {
          start = new Date(now.getFullYear(), now.getMonth(), 1)
        } else if (r.days) {
          start = new Date(now)
          start.setDate(start.getDate() - (r.days - 1))
        } else {
          start = new Date(now)
          start.setDate(start.getDate() - 29)
        }
      } else {
        end = new Date(now)
        start = new Date(now)
        if (r.months > 0) start.setMonth(start.getMonth() - r.months)
        else if (r.months === 0) start.setDate(1)
        else start.setMonth(0, 1)
      }
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      this.dateRange = { start: fmt(start), end: fmt(end) }
      this.customMonthRange = []
      this.customDateRange = []
      this.showDatePicker = false
      this.loadFilters()
      this.loadData()
    },
    applyCustomMonthRange(val) {
      // val 格式: ['YYYY-MM', 'YYYY-MM']
      if (!val || !val[0] || !val[1]) return
      const [startMonth, endMonth] = val
      const [sy, sm] = startMonth.split('-').map(Number)
      const [ey, em] = endMonth.split('-').map(Number)
      // 起始月首日 → 结束月末日（new Date(year, month, 0) 返回上月最后一天）
      const startStr = `${sy}-${String(sm).padStart(2,'0')}-01`
      const endLastDay = new Date(ey, em, 0).getDate()
      const endStr = `${ey}-${String(em).padStart(2,'0')}-${String(endLastDay).padStart(2,'0')}`
      this.dateRange = { start: startStr, end: endStr }
      this.showDatePicker = false
      this.loadFilters()
      this.loadData()
    },
    applyCustomDateRange(val) {
      // val 格式: ['YYYY-MM-DD', 'YYYY-MM-DD']
      if (!val || !val[0] || !val[1]) return
      this.dateRange = { start: val[0], end: val[1] }
      this.showDatePicker = false
      this.loadFilters()
      this.loadData()
    },
    setGranularity(g) {
      if (this.granularity === g) return
      this.granularity = g
      // 切换粒度 → 重置默认范围
      this.customMonthRange = []
      this.customDateRange = []
      const now = new Date()
      if (g === 'Daily') {
        // 默认过去 30 天
        const end = new Date(now)
        const start = new Date(now)
        start.setDate(start.getDate() - 29)
        const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        this.dateRange = { start: fmt(start), end: fmt(end) }
        // Daily 不支持标签维度/筛选
        if (this.groupBy === '标签') this.groupBy = '服务'
        this.groupByTagKey = ''
        this.selectedTagKey = ''
        this.selectedTagValues = []
        this.tagValueOptions = []
      } else {
        // 返回默认 过去 12 个月
        this.initDateRange()
      }
      this.loadFilters()
      this.loadData()
    },
    buildTimeParams() {
      // 根据 granularity 返回后端需要的时间参数
      if (this.granularity === 'Daily') {
        return {
          granularity: 'Daily',
          start_date: this.dateRange.start,
          end_date: this.dateRange.end,
        }
      }
      return {
        granularity: 'Monthly',
        start_month: this.dateToMonth(this.dateRange.start),
        end_month: this.dateToMonth(this.dateRange.end),
      }
    },
    fmtMonth(m) {
      if (m.length === 6) return m.slice(4)
      return m.slice(5)
    },
    exportCsv() {
      if (!this.months.length || !this.services.length) {
        this.$message?.warning('暂无数据可导出')
        return
      }
      const csvEscape = v => {
        const s = v == null ? '' : String(v)
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }
      const fmt = n => (Number(n) || 0).toFixed(2)
      const months = this.months
      const services = [...this.services].sort(
        (a, b) => (this.totalByService[b] || 0) - (this.totalByService[a] || 0)
      )
      // 表头：维度 + 各月 + 总计
      const header = [this.groupBy || '分组', ...months, '总计']
      const rows = [header.map(csvEscape).join(',')]
      // 数据行
      services.forEach(svc => {
        const arr = this.serviceData[svc] || []
        const cells = [svc, ...months.map((_, i) => fmt(arr[i] || 0)), fmt(this.totalByService[svc] || 0)]
        rows.push(cells.map(csvEscape).join(','))
      })
      // 合计行
      const totalRow = ['总成本', ...months.map((_, i) => fmt(this.monthlyTotals[i] || 0)), fmt(this.grandTotal || 0)]
      rows.push(totalRow.map(csvEscape).join(','))
      // 筛选/参数摘要（可选附在表头上方）
      const meta = [
        `# 期间: ${this.dateRange.start} ~ ${this.dateRange.end}`,
        `# 分组: ${this.groupBy}${this.groupByTagKey ? '(标签:' + this.groupByTagKey + ')' : ''}`,
      ]
      const csv = '\ufeff' + meta.join('\n') + '\n' + rows.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `cost_explorer_${this.dateRange.start}_${this.dateRange.end}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    renderChart() {
      const el = this.$refs.mainChartRef
      if (!el) return
      if (!this.months.length || !this.services.length) return
      if (this.mainChart) this.mainChart.dispose()
      this.mainChart = echarts.init(el)
      const months = this.months.map(m => this.fmtMonth(m))
      const fmtVal = v => {
        if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`
        if (v >= 1e3) return `${(v / 1e3).toFixed(0)}k`
        return String(v)
      }
      const series = this.services.map(svc => {
        const data = this.serviceData[svc] || []
        const color = this.serviceColors[svc] || '#9aa5b4'
        if (this.chartType === 'bar') {
          return { name: svc, type: 'bar', stack: 'total', data, itemStyle: { color }, barCategoryGap: '22%' }
        } else if (this.chartType === 'line') {
          return { name: svc, type: 'line', data, smooth: true, lineStyle: { width: 2, color }, itemStyle: { color }, symbol: 'circle', symbolSize: 6 }
        } else {
          return { name: svc, type: 'line', data, smooth: true, stack: 'total', areaStyle: { opacity: 0.55 }, lineStyle: { width: 2, color }, itemStyle: { color } }
        }
      })
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff', borderColor: '#e5e7eb', borderWidth: 1, borderRadius: 6,
          textStyle: { fontSize: 12, color: '#1b1b1f' },
          formatter(params) {
            let html = `<div style="font-weight:600;margin-bottom:4px">Month: ${params[0].axisValue}</div>`
            params.forEach(p => { html += `<div style="display:flex;align-items:center;gap:6px"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${p.color}"></span>${p.seriesName}: <b>$${Number(p.value).toLocaleString()}</b></div>` })
            return html
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08);',
        },
        legend: { bottom: 0, itemWidth: 8, itemHeight: 8, textStyle: { fontSize: 10, color: '#6b7280' }, itemGap: 12 },
        grid: { top: 12, right: 16, bottom: 60, left: 60 },
        xAxis: { type: 'category', data: months, axisLine: { lineStyle: { color: '#e5e7eb' } }, axisTick: { show: false }, axisLabel: { fontSize: 11, color: '#6b7280' } },
        yAxis: { type: 'value', axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }, axisLabel: { fontSize: 11, color: '#6b7280', formatter: v => `$${fmtVal(v)}` } },
        series,
      }
      this.mainChart.setOption(option)
    },
  },
}
</script>

<style lang="scss" scoped>
$font: -apple-system, 'SF Pro Text', 'PingFang SC', 'Microsoft YaHei', sans-serif;
$bg: #f0f2f5;
$card: #ffffff;
$border: #e5e7eb;
$fg: #1b1b1f;
$muted: #6b7280;
$primary: #0073bb;

/* Transitions */
.slide-enter-active, .slide-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  overflow: hidden;
}
.slide-enter-from, .slide-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-enter-to, .slide-leave-from {
  max-height: 500px;
  opacity: 1;
}

.dropdown-enter-active, .dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-enter-from, .dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.cost-explorer-page {
  background: $bg !important;
  min-height: 100%;
  font-family: $font;
  color: $fg;
}

.ce-layout {
  display: flex;
  height: calc(100vh - 90px);
  overflow: hidden;
}

/* ===== LEFT PANEL ===== */
.ce-left-panel {
  width: 260px;
  flex-shrink: 0;
  background: #fafafa;
  border-right: 1px solid $border;
  overflow-y: auto;
  font-size: 13px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid $border;
  background: $card;
  position: sticky;
  top: 0;
  z-index: 10;
}
.panel-title { font-weight: 700; font-size: 15px; color: $fg; }
.panel-close {
  background: none; border: none; cursor: pointer;
  color: $muted; font-size: 12px; padding: 4px 6px; border-radius: 4px;
  &:hover { color: $fg; background: rgba(0,0,0,0.05); }
}

.section-header {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 16px; cursor: pointer;
  border-bottom: 1px solid $border;
  user-select: none;
  transition: background 0.15s;
  &:hover { background: rgba(0,0,0,0.02); }
}
.section-chevron {
  font-size: 11px; color: $muted; width: 12px; display: flex; align-items: center;
  transition: transform 0.25s ease;
  &.expanded { transform: rotate(180deg); }
}
.section-label { font-size: 14px; font-weight: 600; color: $fg; }
.section-badge { margin-left: auto; font-size: 11px; color: $primary; font-weight: 600; }

.section-body { padding: 0 16px 16px; }
.section-body-filters { /* no extra padding, each row has its own */ }
.section-border-b { border-bottom: 1px solid $border; }

.field-group { margin-top: 12px; }
.field-label { display: block; font-size: 13px; color: $fg; font-weight: 600; margin-bottom: 6px; }
.field-row { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; margin-bottom: 4px; }

.clear-link {
  background: none; border: none; cursor: pointer;
  font-size: 12px; color: $primary; padding: 0;
  &:hover { text-decoration: underline; }
}

.panel-select { width: 100%; }
.panel-select :deep(.el-input__wrapper) {
  height: 34px; font-size: 13px; background: $card;
  border-radius: 6px; box-shadow: 0 0 0 1px $border;
}

/* Date picker */
.date-trigger {
  margin-top: 4px; width: 100%; display: flex; align-items: center; gap: 8px;
  border: 1px solid $border; border-radius: 6px; padding: 7px 10px;
  background: $card; font-size: 13px; color: $fg; cursor: pointer;
  transition: border-color 0.15s;
  &:hover { border-color: rgba($primary, 0.5); }
}
.date-icon { color: $muted; font-size: 12px; }
.date-text { flex: 1; text-align: left; }
.trigger-chevron {
  display: flex; align-items: center; color: #c0c4cc; transition: transform 0.25s ease;
  &.expanded { transform: rotate(180deg); }
}

.date-picker-dropdown {
  margin-top: 6px; border: 1px solid $border; border-radius: 6px;
  background: $card; box-shadow: 0 4px 12px rgba(0,0,0,0.08); padding: 12px;
}
.quick-range-title { font-size: 11px; color: $muted; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
.quick-range-btns { display: flex; flex-wrap: wrap; gap: 6px; }
.quick-btn {
  font-size: 11px; padding: 3px 10px; border-radius: 4px;
  border: 1px solid rgba($primary, 0.3); color: $primary; background: none; cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba($primary, 0.06); }
}
.custom-range-block {
  margin-top: 12px; padding-top: 12px; border-top: 1px dashed $border;
}
.custom-range-hint {
  font-size: 11px; color: $muted; margin-top: 6px; line-height: 1.4;
}
.granularity-btns {
  display: inline-flex; gap: 0;
  border: 1px solid $border; border-radius: 6px; overflow: hidden;
}
.gran-btn {
  background: #fff; color: $fg; border: none; padding: 5px 14px;
  font-size: 12px; cursor: pointer; transition: background 0.15s, color 0.15s;
  & + .gran-btn { border-left: 1px solid $border; }
  &:hover { background: rgba($primary, 0.06); }
  &.active { background: $primary; color: #fff; }
}

/* Filters */
.filter-section-bar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 16px; border-bottom: 1px solid $border; background: rgba(0,115,187,0.03);
}
.filter-applied-text { font-size: 12px; color: $fg; font-weight: 500; }
.clear-all-link {
  background: none; border: none; cursor: pointer;
  font-size: 11px; color: $primary; padding: 0;
  &:hover { text-decoration: underline; }
}

.aws-filter-row {
  padding: 8px 16px; border-bottom: 1px solid $border;
}
.aws-filter-label-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 4px;
}
.aws-filter-label { font-size: 13px; font-weight: 600; color: $fg; }
.aws-filter-select-wrap { position: relative; }
.aws-filter-trigger {
  width: 100%; display: flex; align-items: center; justify-content: space-between;
  border: 1px solid $border; border-radius: 6px; padding: 7px 10px;
  background: $card; font-size: 13px; color: $muted; cursor: pointer;
  transition: border-color 0.15s;
  &:hover { border-color: rgba($primary, 0.5); }
}
.aws-filter-placeholder {
  flex: 1; text-align: left;
  &.has-value { color: $fg; font-weight: 500; }
}
.aws-filter-arrow {
  flex-shrink: 0; color: $muted; transition: transform 0.25s ease;
  &.expanded { transform: rotate(180deg); }
}
.aws-filter-panel {
  position: absolute; z-index: 50; left: 0; right: 0; top: 100%; margin-top: 2px;
  background: $card; border: 1px solid $border; border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
}

.filter-search-wrap { padding: 8px; border-bottom: 1px solid $border; }
.filter-search-input {
  width: 100%; padding: 5px 8px; font-size: 12px;
  border: 1px solid $border; border-radius: 4px; background: $bg;
  &:focus { outline: none; box-shadow: 0 0 0 1px $primary; }
}
.filter-options { max-height: 200px; overflow-y: auto; padding: 4px 0; }
.no-results { font-size: 11px; color: $muted; text-align: center; padding: 12px 0; }
.filter-option {
  display: flex; align-items: center; gap: 8px; padding: 5px 12px; cursor: pointer;
  font-size: 12px; color: $fg; transition: background 0.1s;
  &:hover { background: #f5f5f5; }
  input { width: 14px; height: 14px; accent-color: $primary; }
}
.fixed-value {
  margin-top: 4px; padding: 6px 10px; font-size: 12px; color: $fg;
  background: $card; border: 1px solid $border; border-radius: 4px;
}

/* ===== RIGHT CONTENT ===== */
.ce-right-content { flex: 1; overflow-y: auto; display: flex; flex-direction: column; }

.sub-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 20px; background: $card; border-bottom: 1px solid $border;
}
.sub-title { font-size: 15px; font-weight: 600; color: $fg; margin: 0; }
.sub-actions { display: flex; gap: 8px; }
.btn-outline {
  background: $card; border: 1px solid $border; border-radius: 4px;
  padding: 4px 12px; font-size: 12px; color: $fg; cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f5f5f5; }
}
.btn-sm { height: 28px; display: inline-flex; align-items: center; gap: 6px; }

.main-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }

/* Chart card */
.chart-card {
  background: $card; border: 1px solid $border; border-radius: 8px;
  padding: 20px;
}
.chart-card-header { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 12px; }
.chart-card-title { font-size: 14px; font-weight: 600; color: $fg; margin: 0; }
.info-link { font-size: 11px; color: $primary; cursor: pointer; &:hover { text-decoration: underline; } }

.kpi-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; margin-bottom: 16px; }
.kpi-label { font-size: 11px; color: $muted; margin: 0 0 2px; }
.kpi-value { font-size: 22px; font-weight: 600; color: $fg; margin: 0; font-variant-numeric: tabular-nums; line-height: 1.2; }

.chart-type-toggle { display: flex; align-items: center; justify-content: flex-end; gap: 4px; margin-bottom: 12px; }
.costs-label { font-size: 11px; color: $muted; margin-right: 8px; }
.toggle-group { display: flex; border: 1px solid $border; border-radius: 4px; overflow: hidden; }
.toggle-btn {
  padding: 6px 10px; border: none; border-right: 1px solid $border; cursor: pointer;
  font-size: 12px; background: $card; color: $muted; transition: all 0.15s;
  &:last-child { border-right: none; }
  &.active { background: $primary; color: #fff; }
  &:hover:not(.active) { background: #f5f5f5; }
}

.chart-container { width: 100%; height: 320px; }

/* Breakdown table */
.breakdown-card { background: $card; border: 1px solid $border; border-radius: 8px; }
.breakdown-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px; border-bottom: 1px solid $border;
}
.breakdown-title { font-size: 14px; font-weight: 600; color: $fg; margin: 0; }
.search-wrap { position: relative; }
.search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 12px; }
.search-input {
  padding: 6px 12px 6px 30px; font-size: 12px; border: 1px solid $border;
  border-radius: 6px; background: $bg; width: 176px;
  &:focus { outline: none; box-shadow: 0 0 0 1px $primary; }
}

.table-wrap { overflow-x: auto; }
.breakdown-table {
  width: 100%; font-size: 12px; border-collapse: collapse;
  th, td { padding: 10px 12px; white-space: nowrap; }
  thead tr { border-bottom: 1px solid $border; background: #f8f9fa; }
}
.th-left { text-align: left; font-weight: 500; color: $muted; padding-left: 20px; }
.th-right { text-align: right; font-weight: 500; color: $muted; }
.td-left { text-align: left; padding-left: 20px; display: flex; align-items: center; gap: 8px; }
.td-right { text-align: right; }
.td-bold { font-weight: 600; color: $fg; }
.td-medium { font-weight: 500; color: $fg; }
.td-muted { color: $muted; }
.font-data { font-variant-numeric: tabular-nums; }
.total-row { border-bottom: 1px solid $border; background: rgba(0,0,0,0.02); }
.data-row {
  border-bottom: 1px solid $border;
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(0,0,0,0.015); }
}
.color-dot { display: inline-block; width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; }
</style>
